import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { Alert, Button, Card, notification } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import "dayjs/locale/lt";
import dayLocaleData from "dayjs/plugin/localeData";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import debounce from "lodash.debounce";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import type { Dayjs } from "dayjs";
import type { InputRef } from "antd";
import { ValidationError } from "yup";
import { UserFormInterface, formSchema } from "../definitions";

import Step1 from "../steps/Step1";
import Step2 from "../steps/Step2";
import Step3 from "../steps/Step3";
import Step4 from "../steps/Step4";
import Step5 from "../steps/Step5";

// Extend Day.js with necessary plugins
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(dayLocaleData);

interface City {
  uuid: string;
  name: string;
}

interface MultiStepFormProps {
  onSubmitSuccess: () => void;
  reservationType: string | null;
  formConfig?: import("../App").FormConfig | null;
}

const MultiStepForm: React.FC<MultiStepFormProps> = ({
  onSubmitSuccess,
  reservationType,
  formConfig,
}) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  dayjs.locale("lt");

  // API Endpoints
  // const apiBaseUrl = "https://erdve.hexomeda.com";
  // const apiBaseUrl = "http://localhost:5000";
  const apiBaseUrl = process.env.REACT_APP_API_URL;
  const servicesEndpoint = `${apiBaseUrl}/api/service/get/all`;
  const invitesEndPoint = `${apiBaseUrl}/api/invitations/by_reservation_type`;
  const timesEndpoint = `${apiBaseUrl}/api/calendar/get/freetimes`;
  const discountEndpoint = `${apiBaseUrl}/api/discount/check`;
  const formEndpoint = `${apiBaseUrl}/api/order/add`;
  const citiesEndpoint = `${apiBaseUrl}/api/cities`;

  const guestOverflow = 14;

  // State management
  const [currentStep, setCurrentStep] = useState(0);
  const [services, setServices] = useState<any[]>([]);
  const [price, setPrice] = useState(0);
  const [discountStatus, setDiscountStatus] = useState<Boolean | null>(null);
  const [invitations, setInvitations] = useState<any[]>([]);
  const [discount, setDiscount] = useState({
    discount_amount: 0,
    uuid: "",
  });
  const [loading, setLoading] = useState(true);
  const [activeTime, setActiveTime] = useState<string | null>(null);
  const [activeInvite, setActiveInvite] = useState<string | null>(null);
  const [availableTimes, setAvailableTimes] = useState<any[]>([]);
  const [isPosting, setPosting] = useState<boolean>(false);
  const [criticalError, setCriticalError] = useState<boolean>(false);
  const [combo, setCombo] = useState<boolean>(false);
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [cities, setCities] = useState<City[]>([]);
  const [nonCriticalError, setNonCriticalError] = useState(false);
  const [autoDiscount, setAutoDiscount] = useState(0);
  const [confirmationChecked, setConfirmationChecked] = useState(false);
  const [isExtensionEnabled, setIsExtensionEnabled] = useState(false);
  const [isFirstTimeSlotSelected, setIsFirstTimeSlotSelected] = useState(false);
  const [maxExtensionMinutes, setMaxExtensionMinutes] = useState(0);
  const [canAdjustStart, setCanAdjustStart] = useState(false);
  const [canAdjustEnd, setCanAdjustEnd] = useState(false);
  const discountRef = useRef<InputRef>(null);

  interface ErrorInterface {
    [key: string]: string;
  }
  const [errors, setErrors] = useState<ErrorInterface>({});

  const [userForm, setUserForm] = useState<UserFormInterface>({
    customer_email: "",
    customer_parent_name: "",
    customer_name: "",
    customer_age: 0,
    child_birth_date: "",
    customer_phone_no: "",
    day_selected: "",
    customer_address: "",
    customer_guests: 0,
    time_selected: "",
    invitation_type: "",
    discount_code: "",
    services_selected: [],
    print_invites: "false",
    phone_in_invitation: "false",
    city_uuid: "",
    // Consent fields
    marketing_consent: "false",
    data_processing_consent: "true",
    // Dvynių / dviejų vaikų gimtadienis
    is_twin_birthday: false,
    is_two_children_birthday: false,
    child_name_2: "",
    child_birth_date_2: "",
    child_age_2: 0,
  });

  // Step validation logic
  const validateStep = (stepIndex: number): boolean => {
    const newErrors: ErrorInterface = {};

    switch (stepIndex) {
      case 0: // Step 1: City, Date, Time
        if (!selectedCity) {
          newErrors.city_uuid = "Pasirinkite miestą";
        }
        if (!userForm.day_selected) {
          newErrors.day_selected = "Pasirinkite datą";
        }
        if (!activeTime) {
          newErrors.time_selected = "Pasirinkite laiką";
        }
        break;
      case 1: // Step 2: Contact Information
        if (!userForm.customer_name) {
          newErrors.customer_name = "Vaiko vardas yra privalomas";
        }
        if (!userForm.child_birth_date) {
          newErrors.child_birth_date = "Gimimo data yra privaloma";
        } else {
          // Check if child will be celebrating at least their 4th birthday
          const birthdayNumber = calculateBirthdayNumber(
            userForm.child_birth_date,
            userForm.day_selected || dayjs().format('YYYY-MM-DD')
          );
          
          if (birthdayNumber < 4) {
            newErrors.child_birth_date = "Vaiko amžius turi būti bent 4 metai";
          }
        }
        if (!userForm.customer_guests || userForm.customer_guests <= 0) {
          newErrors.customer_guests = "Vaikų skaičius yra privalomas";
        }
        if (!userForm.customer_parent_name) {
          newErrors.customer_parent_name = "Tėvo/mamos vardas yra privalomas";
        }
        if (!userForm.customer_address) {
          newErrors.customer_address = "Adresas yra privalomas";
        } else if (userForm.customer_address.length < 5) {
          newErrors.customer_address = "Adresas turi būti bent 5 simbolių";
        }
        if (!userForm.customer_phone_no) {
          newErrors.customer_phone_no = "Telefono numeris yra privalomas";
        }
        if (!userForm.customer_email) {
          newErrors.customer_email = "El. paštas yra privalomas";
        } else {
          // Email format validation
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(userForm.customer_email)) {
            newErrors.customer_email = "El. pašto formatas neteisingas";
          }
        }
        // Validate second child fields when two children birthday is selected
        if (userForm.is_two_children_birthday) {
          if (!userForm.child_name_2 || userForm.child_name_2.length < 2) {
            newErrors.child_name_2 = "Antro vaiko vardas yra privalomas";
          }
          if (!userForm.child_birth_date_2) {
            newErrors.child_birth_date_2 = "Antro vaiko gimimo data yra privaloma";
          } else {
            const birthdayNumber2 = calculateBirthdayNumber(
              userForm.child_birth_date_2,
              userForm.day_selected || dayjs().format('YYYY-MM-DD')
            );
            if (birthdayNumber2 < 4) {
              newErrors.child_birth_date_2 = "Antro vaiko amžius turi būti bent 4 metai";
            }
          }
        }
        break;
      case 2: // Step 3: Services - no validation needed
        break;
      case 3: // Step 4: Invitations
        if (invitations.length > 0 && !activeInvite) {
          newErrors.invitation_type = "Būtina pasirinkti pakvietimą";
        }
        break;
      case 4: // Step 5: Summary and Submit - no additional validation
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Navigation handlers
  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const goToStep = (stepIndex: number) => {
    // Only allow navigation to completed steps or current step
    if (stepIndex <= currentStep) {
      setCurrentStep(stepIndex);
    }
  };

  // Fetch cities on mount
  useEffect(() => {
    let isMounted = true;

    if (isMounted) {
      axios
        .get(citiesEndpoint)
        .then((res) => {
          // Now expecting direct array format
          setCities(res.data);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching cities: ", err);
          setCriticalError(true);
          setLoading(false);
        });
    }

    return () => {
      isMounted = false;
    };
  }, [citiesEndpoint]);

  // Fetch services based on selected city only (services are for birthday parties only)
  const fetchServices = useCallback(
    (cityUUID: string | null) => {
      const params: any = {};

      if (cityUUID) {
        params.city_uuid = cityUUID;
      }

      // Filter by reservation type to only show relevant services
      if (reservationType) {
        params.reservation_type = reservationType;
      }

      axios
        .get(servicesEndpoint, { params })
        .then((res) => {
          setServices(res.data);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching services: ", err);
          setCriticalError(true);
        });
    },
    [servicesEndpoint, reservationType]
  );

  // Fetch services when city changes (services are only for birthday parties)
  useEffect(() => {
    if (selectedCity) {
      fetchServices(selectedCity.uuid);
    } else {
      // Fetch all services if no city selected
      fetchServices(null);
    }
  }, [selectedCity, fetchServices]);

  const fetchInvitationsByReservationType = useCallback(
    (resTypeUuid: string) => {
      const fullUrl = `${invitesEndPoint}/${resTypeUuid}`;

      axios
        .get(fullUrl)
        .then((res) => {
          setInvitations(res.data);
        })
        .catch((err) => {
          console.error("Error fetching invitations: ", err);
          console.error("Failed URL:", fullUrl);
          // Don't set critical error - just means no invitations for this type
          setInvitations([]);
        });
    },
    [invitesEndPoint]
  );

  useEffect(() => {
    if (reservationType) {
      fetchInvitationsByReservationType(reservationType);
    }
  }, [reservationType, fetchInvitationsByReservationType]);

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      return;
    }
    await executeRecaptcha("yourAction");
  }, [executeRecaptcha]);

  useEffect(() => {
    handleReCaptchaVerify();
  }, [handleReCaptchaVerify]);

  const debouncedFetchTimes = useMemo(
    () =>
      debounce((cityUUID: string, date: string) => {
        const params: any = {
          date: date,
          city_uuid: cityUUID,
        };

        // Add reservation_type if available
        if (reservationType) {
          params.reservation_type = reservationType;
        }

        axios
          .get(timesEndpoint, { params })
          .then((res) => {
            setAvailableTimes(res.data);
            setUserForm((prevUserForm) => ({
              ...prevUserForm,
              day_selected: date,
            }));
            setLoading(false);
          })
          .catch((error) => {
            console.error("Error fetching available times:", error);
            setCriticalError(true);
            setLoading(false);
          });
      }, 300),
    [timesEndpoint, reservationType]
  );

  // Disable past dates in the calendar
  const calendarFilter = (current: Dayjs): boolean => {
    return current && current < dayjs().startOf("day");
  };

  const handleCityChange = (city: City) => {
    setSelectedCity(city);

    // Reset entire form when city changes
    setUserForm({
      customer_email: "",
      customer_parent_name: "",
      customer_name: "",
      customer_age: 0,
      child_birth_date: "",
      customer_phone_no: "",
      day_selected: "",
      customer_address: "",
      customer_guests: 0,
      time_selected: "",
      invitation_type: "",
      discount_code: "",
      services_selected: [],
      print_invites: "false",
      phone_in_invitation: "false",
      city_uuid: city.uuid,
      // BDAR consent fields
      marketing_consent: "false",
      data_processing_consent: "false",
      // Dvynių / dviejų vaikų gimtadienis
      is_twin_birthday: false,
      is_two_children_birthday: false,
      child_name_2: "",
      child_birth_date_2: "",
      child_age_2: 0,
    });

    // Reset all other state
    setActiveTime(null);
    setActiveInvite(null);
    setAvailableTimes([]);
    setPrice(0);
    setCombo(false);
    setDiscountStatus(null);
    setDiscount({ discount_amount: 0, uuid: "" });
    setAutoDiscount(0);
    setIsExtensionEnabled(false);
    setIsFirstTimeSlotSelected(false);
    setMaxExtensionMinutes(0);
    setCanAdjustStart(false);
    setCanAdjustEnd(false);
    setErrors({});
    setLoading(true);

    const selectedDate = dayjs().add(1, "day").format("YYYY-MM-DD");
    debouncedFetchTimes(city.uuid, selectedDate);
  };

  const calendarChange = (e: Dayjs) => {
    setActiveTime(null);
    setIsExtensionEnabled(false);
    setIsFirstTimeSlotSelected(false);
    setMaxExtensionMinutes(0);
    setCanAdjustStart(false);
    setCanAdjustEnd(false);
    const newDate = e.format("YYYY-MM-DD");

    setUserForm((prevUserForm) => {
      const updatedForm = {
        ...prevUserForm,
        time_selected: null,
        day_selected: newDate,
      };

      // Recalculate birthday number if birth date exists
      if (prevUserForm.child_birth_date) {
        const birthdayNumber = calculateBirthdayNumber(
          prevUserForm.child_birth_date,
          newDate
        );
        updatedForm.customer_age = birthdayNumber;
      }

      return updatedForm;
    });

    if (!selectedCity) {
      setErrors({ city_uuid: "Please select a city" });
      return;
    }
    setLoading(true);

    const params: any = {
      date: e.format("YYYY-MM-DD"),
      city_uuid: selectedCity.uuid,
    };

    // Add reservation_type if available
    if (reservationType) {
      params.reservation_type = reservationType;
    }

    axios
      .get(timesEndpoint, { params })
      .then((res) => {
        if (res.data && res.data.length > 0) {
          setAvailableTimes(res.data);
          setNonCriticalError(false);
        } else {
          setAvailableTimes([]);
          setNonCriticalError(false);
        }

        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching times: ", err);
        setNonCriticalError(true);
        setLoading(false);
      });
  };

  const handleCalendarErrorReset = () => {
    const nextDay = dayjs().add(1, "day");

    setUserForm({
      ...userForm,
      day_selected: nextDay.format("YYYY-MM-DD"),
    });

    if (selectedCity) {
      setLoading(true);

      const params: any = {
        date: nextDay.format("YYYY-MM-DD"),
        city_uuid: selectedCity.uuid,
      };

      // Add reservation_type if available
      if (reservationType) {
        params.reservation_type = reservationType;
      }

      axios
        .get(timesEndpoint, { params })
        .then((res) => {
          setAvailableTimes(res.data);
          setActiveTime(null);
          setIsExtensionEnabled(false);
          setIsFirstTimeSlotSelected(false);
          setMaxExtensionMinutes(0);
          setCanAdjustStart(false);
          setCanAdjustEnd(false);
          setNonCriticalError(false);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching times for the next day: ", err);
          setLoading(false);
        });
    } else {
      setNonCriticalError(false);
    }
  };

  const handleTimeSelect = (timeUuid: string, basePrice: number) => {
    setActiveTime(timeUuid);

    const selectedTime = availableTimes.find((time) => time.uuid === timeUuid);

    // Determine extension capabilities from time slot configuration
    const extensionsEnabled = selectedTime?.allow_start_adjustment || selectedTime?.allow_end_adjustment;
    setIsExtensionEnabled(!!extensionsEnabled);
    // Start adjustment means party starts earlier; end adjustment means party ends later
    setIsFirstTimeSlotSelected(!!selectedTime?.allow_start_adjustment && !selectedTime?.allow_end_adjustment);

    const maxMins = Math.max(
      selectedTime?.allow_start_adjustment ? (selectedTime?.start_adjustment_minutes || 0) : 0,
      selectedTime?.allow_end_adjustment ? (selectedTime?.end_adjustment_minutes || 0) : 0
    );
    setMaxExtensionMinutes(maxMins);
    setCanAdjustStart(!!selectedTime?.allow_start_adjustment);
    setCanAdjustEnd(!!selectedTime?.allow_end_adjustment);

    // Remove extension services that are invalid for this time slot
    const invalidExtensionUuids = services
      .filter((s: any) => {
        if (!s.extension_minutes) return false;
        if (!extensionsEnabled) return true;
        return s.extension_minutes > maxMins;
      })
      .map((s: any) => s.uuid);

    if (invalidExtensionUuids.length > 0) {
      setUserForm((prevUserForm) => ({
        ...prevUserForm,
        time_selected: timeUuid,
        services_selected: prevUserForm.services_selected.filter(
          (uuid) => !invalidExtensionUuids.includes(uuid)
        ),
      }));
    } else {
      setUserForm((prevUserForm) => ({
        ...prevUserForm,
        time_selected: timeUuid,
      }));
    }

    setPrice(basePrice);
  };

  // Function to calculate birthday number based on birth date and party date
  // Returns the birthday number they're celebrating (the closest birthday to party date)
  const calculateBirthdayNumber = (
    birthDate: string,
    partyDate: string
  ): number => {
    if (!birthDate || !partyDate) {
      return 0;
    }

    const birth = dayjs(birthDate);
    const party = dayjs(partyDate);

    if (!birth.isValid() || !party.isValid()) {
      return 0;
    }

    // Get birthdays in adjacent years to find the closest one
    const birthdayLastYear = birth.year(party.year() - 1);
    const birthdayThisYear = birth.year(party.year());
    const birthdayNextYear = birth.year(party.year() + 1);

    // Calculate days distance from party to each birthday
    const diffLastYear = Math.abs(party.diff(birthdayLastYear, "day"));
    const diffThisYear = Math.abs(party.diff(birthdayThisYear, "day"));
    const diffNextYear = Math.abs(party.diff(birthdayNextYear, "day"));

    // Find the closest birthday and return that birthday number
    if (diffThisYear <= diffLastYear && diffThisYear <= diffNextYear) {
      return party.year() - birth.year();
    } else if (diffLastYear <= diffNextYear) {
      return party.year() - 1 - birth.year();
    } else {
      return party.year() + 1 - birth.year();
    }
  };

  const fillableForm = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    const updatedForm = { ...userForm, [name]: value };

    // If birth date changes, recalculate age
    if (name === "child_birth_date" && value && userForm.day_selected) {
      const birthdayNumber = calculateBirthdayNumber(
        value,
        userForm.day_selected
      );
      updatedForm.customer_age = birthdayNumber;
    }

    // If party date changes, recalculate age if birth date exists
    if (name === "day_selected" && value && userForm.child_birth_date) {
      const birthdayNumber = calculateBirthdayNumber(
        userForm.child_birth_date,
        value
      );
      updatedForm.customer_age = birthdayNumber;
    }

    setUserForm(updatedForm);
  };

  const handleServiceChange = (
    serviceUuid: string,
    servicePrice: number,
    isChecked: boolean
  ) => {
    let array: string[] = [...userForm.services_selected];

    if (array.includes(serviceUuid)) {
      array.splice(array.indexOf(serviceUuid), 1);
    } else {
      array.push(serviceUuid);
    }

    if (isChecked) {
      setPrice((price) => price + servicePrice);
    } else {
      setPrice((price) => price - servicePrice);
    }

    setUserForm({
      ...userForm,
      services_selected: array,
    });

    // Combo selection logic
    if (
      array.includes("fe2b259a25da426db77eca7b0aa50ba5") &&
      array.includes("2c16e8c047734c72999241d3c88d481a")
    ) {
      setCombo(true);
    } else {
      setCombo(false);
    }
  };

  const handleInvitationSelect = (inviteUuid: string) => {
    setUserForm({
      ...userForm,
      invitation_type: inviteUuid,
    });
    setActiveInvite(inviteUuid);
  };

  const handlePrintInvitesChange = (checked: boolean) => {
    setUserForm({
      ...userForm,
      print_invites: `${checked}`,
      // When unchecking print_invites, also uncheck phone_in_invitation
      phone_in_invitation: checked ? userForm.phone_in_invitation : "false",
    });
  };

  const handlePhoneInInvitationChange = (checked: boolean) => {
    setUserForm({
      ...userForm,
      phone_in_invitation: `${checked}`,
    });
  };

  const handleMarketingConsentChange = (checked: boolean) => {
    setUserForm({
      ...userForm,
      marketing_consent: `${checked}`,
    });
  };

  const handleConfirmationChange = (checked: boolean) => {
    setConfirmationChecked(checked);
  };

  const handleBirthDateChange = (
    date: dayjs.Dayjs | null,
    dateString: string
  ) => {
    const updatedForm = { ...userForm, child_birth_date: dateString };

    // Calculate birthday number if party date is selected
    if (dateString && userForm.day_selected) {
      const birthdayNumber = calculateBirthdayNumber(
        dateString,
        userForm.day_selected
      );
      updatedForm.customer_age = birthdayNumber;
    }

    setUserForm(updatedForm);
  };

  const handleTwinBirthdayChange = (checked: boolean) => {
    setUserForm((prev) => ({
      ...prev,
      is_twin_birthday: checked,
      // Mutually exclusive: uncheck two_children if twin is checked
      is_two_children_birthday: checked ? false : prev.is_two_children_birthday,
      // Clear second child fields when twin is checked (single invitation with combined name)
      child_name_2: checked ? "" : prev.child_name_2,
      child_birth_date_2: checked ? "" : prev.child_birth_date_2,
      child_age_2: checked ? 0 : prev.child_age_2,
    }));
  };

  const handleTwoChildrenBirthdayChange = (checked: boolean) => {
    setUserForm((prev) => ({
      ...prev,
      is_two_children_birthday: checked,
      // Mutually exclusive: uncheck twin if two_children is checked
      is_twin_birthday: checked ? false : prev.is_twin_birthday,
      // Clear second child fields when unchecked
      child_name_2: checked ? prev.child_name_2 : "",
      child_birth_date_2: checked ? prev.child_birth_date_2 : "",
      child_age_2: checked ? prev.child_age_2 : 0,
    }));
  };

  const handleBirthDateChange2 = (
    date: dayjs.Dayjs | null,
    dateString: string
  ) => {
    const updatedForm = { ...userForm, child_birth_date_2: dateString };

    if (dateString && userForm.day_selected) {
      const birthdayNumber = calculateBirthdayNumber(
        dateString,
        userForm.day_selected
      );
      updatedForm.child_age_2 = birthdayNumber;
    }

    setUserForm(updatedForm);
  };

  useEffect(() => {
    if (combo === true) {
      setPrice((price) => price - 10);
    } else if (price > 0) {
      // Only add back 10 EUR if price was already set (not initial render)
      setPrice((price) => price + 10);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [combo]);

  // Updated calculateDiscount function to include automatic discounts
  const calculateDiscount = useCallback(() => {
    let discountAmount = 0;

    // Apply combo discount
    if (combo) {
      discountAmount = 0; // Assuming combo discount is fixed at 10 EUR
    }

    // Apply discount code amount
    if (discount.discount_amount > 0) {
      discountAmount += discount.discount_amount;
    }

    // Apply automatic discount
    if (autoDiscount > 0) {
      discountAmount += autoDiscount;
    }

    return discountAmount;
  }, [combo, discount.discount_amount, autoDiscount]);

  const totalDiscount = calculateDiscount();

  // Auto discount reset (no automatic discounts currently active)
  useEffect(() => {
    if (autoDiscount !== 0) {
      setAutoDiscount(0);
    }
  }, [selectedCity, userForm.day_selected, autoDiscount]);

  const discountedPrice = price - totalDiscount;

  const handleDiscountCodeCheck = () => {
    if (
      userForm.discount_code !== "" &&
      selectedCity &&
      userForm.day_selected
    ) {
      axios
        .get(discountEndpoint, {
          params: {
            code: userForm.discount_code,
            city_uuid: selectedCity.uuid,
            day_selected: userForm.day_selected,
          },
        })
        .then((res) => {
          if (res.data.status === 200) {
            setDiscountStatus(true);
            setDiscount(res.data.discount);
          } else {
            setDiscountStatus(false);
            setDiscount({
              discount_amount: 0,
              uuid: "",
            });

            notification.error({
              message: "Nuolaidos Kodo Klaida",
              description:
                "Nuolaidos kodas nerastas arba galiojimo laikotarpis pasibaigęs.",
              placement: "topRight",
            });
          }
        })
        .catch((err) => {
          console.error("Error checking discount code: ", err);
          setDiscountStatus(false);
          setDiscount({
            discount_amount: 0,
            uuid: "",
          });

          notification.error({
            message: "Nuolaidos Kodo Klaida",
            description:
              "Klaida tikrinant nuolaidos kodą. Prašome bandyti vėliau.",
            placement: "topRight",
          });
        });
    } else {
      notification.warning({
        message: "Data Nepasirinkta",
        description:
          "Pirma pasirinkite datą, kad galėtumėte tikrinti nuolaidos kodą.",
        placement: "topRight",
      });
    }
  };

  const postData = () => {
    setErrors({});
    if (!selectedCity) {
      setErrors({ city_uuid: "Please select a city" });
      return;
    }

    setPosting(true);
    setCriticalError(false);
    formSchema
      .validate(
        { ...userForm, city_uuid: selectedCity.uuid },
        { abortEarly: false }
      )
      .then(() => {
        axios
          .post(formEndpoint, {
            ...userForm,
            city_uuid: selectedCity.uuid,
            is_first_time_slot: isFirstTimeSlotSelected,
            is_twin_birthday: userForm.is_twin_birthday,
            is_two_children_birthday: userForm.is_two_children_birthday,
            child_name_2: userForm.child_name_2 || null,
            child_birth_date_2: userForm.child_birth_date_2 || null,
            child_age_2: userForm.child_age_2 || null,
          })
          .then((res) => {
            if (res.data.statusCode !== 200) {
              console.error("Critical error posting data: ", res.data);
              setCriticalError(true);

              // Extract error message from payload
              const errorPayload = res.data.payload;
              let errorMsg = "Nepavyko pateikti registracijos. Bandykite dar kartą.";
              if (errorPayload) {
                if (typeof errorPayload === 'object') {
                  const firstError = Object.values(errorPayload).flat()[0];
                  if (typeof firstError === 'string') errorMsg = firstError;
                } else if (typeof errorPayload === 'string') {
                  errorMsg = errorPayload;
                }
              }

              notification.error({
                message: "Registracijos klaida",
                description: errorMsg,
                placement: "topRight",
                duration: 8,
              });
            } else {
              onSubmitSuccess();
            }
          })
          .catch((err) => {
            console.error("Error posting data: ", err);
            setCriticalError(true);
            notification.error({
              message: "Registracijos klaida",
              description: "Nepavyko pateikti registracijos. Bandykite dar kartą arba susisiekite su mumis.",
              placement: "topRight",
              duration: 8,
            });
          })
          .finally(() => {
            setPosting(false);
          });
      })
      .catch((error) => {
        if (error instanceof ValidationError) {
          let obj: ErrorInterface = {};
          for (const e of error.inner) {
            if (e.path !== undefined) {
              obj[e.path] = e.message;
            }
          }
          setErrors(obj);
        }
        console.error("Validation error: ", error);
        setPosting(false);
      });
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <Step1
            cities={cities}
            selectedCity={selectedCity}
            availableTimes={availableTimes}
            activeTime={activeTime}
            loading={loading}
            onCityChange={handleCityChange}
            onCalendarChange={calendarChange}
            onTimeSelect={handleTimeSelect}
            calendarFilter={calendarFilter}
            nonCriticalError={nonCriticalError}
            onCalendarErrorReset={handleCalendarErrorReset}
            isPosting={isPosting}
            errors={errors}
          />
        );
      case 1:
        return (
          <Step2
            userForm={userForm}
            errors={errors}
            fillableForm={fillableForm}
            onBirthDateChange={handleBirthDateChange}
            onBirthDateChange2={handleBirthDateChange2}
            onTwinBirthdayChange={handleTwinBirthdayChange}
            onTwoChildrenBirthdayChange={handleTwoChildrenBirthdayChange}
            activeTime={activeTime}
            isPosting={isPosting}
            guestOverflow={guestOverflow}
          />
        );
      case 2:
        return (
          <Step3
            services={services}
            userForm={userForm}
            combo={combo}
            activeTime={activeTime}
            isExtensionEnabled={isExtensionEnabled}
            isFirstTimeSlotSelected={isFirstTimeSlotSelected}
            isPosting={isPosting}
            onServiceChange={handleServiceChange}
            maxExtensionMinutes={maxExtensionMinutes}
            canAdjustStart={canAdjustStart}
            canAdjustEnd={canAdjustEnd}
          />
        );
      case 3:
        return (
          <Step4
            invitations={invitations}
            userForm={userForm}
            activeInvite={activeInvite}
            activeTime={activeTime}
            isPosting={isPosting}
            errors={errors}
            apiBaseUrl={apiBaseUrl || ""}
            onInvitationSelect={handleInvitationSelect}
            onPrintInvitesChange={handlePrintInvitesChange}
            onPhoneInInvitationChange={handlePhoneInInvitationChange}
          />
        );
      case 4:
        return (
          <Step5
            userForm={userForm}
            selectedCity={selectedCity}
            availableTimes={availableTimes}
            services={services}
            invitations={invitations}
            activeTime={activeTime}
            activeInvite={activeInvite}
            price={price}
            totalDiscount={totalDiscount}
            discountedPrice={discountedPrice}
            autoDiscount={autoDiscount}
            discountStatus={discountStatus}
            isPosting={isPosting}
            discountRef={discountRef}
            fillableForm={fillableForm}
            onDiscountCheck={handleDiscountCodeCheck}
            onSubmit={postData}
            onPrevStep={prevStep}
            apiBaseUrl={apiBaseUrl || ""}
            combo={combo}
            confirmationChecked={confirmationChecked}
            onConfirmationChange={handleConfirmationChange}
            marketingConsent={userForm.marketing_consent === "true"}
            onMarketingConsentChange={handleMarketingConsentChange}
            isFirstTimeSlotSelected={isFirstTimeSlotSelected}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div style={{ maxWidth: "400px", fontFamily: "Poppins, sans-serif" }}>
      {/* Clickable steps indicator */}
      <Card
        style={{ marginBottom: "16px", textAlign: "center" }}
        bodyStyle={{ padding: "12px" }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "8px",
          }}
        >
          {[1, 2, 3, 4, 5].map((step) => {
            const stepIndex = step - 1;
            const isCompleted = currentStep > stepIndex;
            const isCurrent = currentStep === stepIndex;
            const isClickable = stepIndex <= currentStep;

            return (
              <div
                key={step}
                onClick={() => (isClickable ? goToStep(stepIndex) : null)}
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: isCurrent
                    ? "#66469c"
                    : isCompleted
                    ? "#9cc43c"
                    : "#f0f0f0",
                  color: isCurrent || isCompleted ? "white" : "#666",
                  fontWeight: "bold",
                  fontSize: "14px",
                  cursor: isClickable ? "pointer" : "default",
                  transition: "all 0.2s ease",
                  border: isClickable
                    ? "2px solid transparent"
                    : "2px solid #f0f0f0",
                }}
                onMouseEnter={(e) => {
                  if (isClickable) {
                    e.currentTarget.style.transform = "scale(1.1)";
                    e.currentTarget.style.borderColor = isCurrent
                      ? "#1890ff"
                      : isCompleted
                      ? "#52c41a"
                      : "#d9d9d9";
                  }
                }}
                onMouseLeave={(e) => {
                  if (isClickable) {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.borderColor = "transparent";
                  }
                }}
                title={
                  isClickable ? `Eiti į ${step} žingsnį` : `${step} žingsnis`
                }
              >
                {step}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Critical error display */}
      {criticalError && (
        <Alert
          type="error"
          message="Klaida"
          description="Nepavyko užkrauti duomenų. Bandykite perkrauti puslapį."
          showIcon
          style={{ marginBottom: "16px" }}
        />
      )}

      {/* Current step content */}
      {renderCurrentStep()}

      {/* Navigation buttons - Hide for Step 5 as it has its own navigation */}
      {currentStep < 4 && (
        <Card style={{ marginTop: "16px", textAlign: "center" }}>
          {currentStep > 0 && (
            <Button onClick={prevStep} style={{ marginRight: "8px" }}>
              Atgal
            </Button>
          )}
          <Button type="primary" onClick={nextStep}>
            Kitas žingsnis
          </Button>
        </Card>
      )}
    </div>
  );
};

export default MultiStepForm;
